// src\components\dashboard\BatchDetail.jsx
import React from 'react';
import { Clock, CheckCircle, AlertTriangle, Clock3 } from 'lucide-react';
import { useData } from '../../contexts/DataContext';

const BatchDetail = ({ batch, orders, resource, isSelected, onSelect }) => {
  const { updateOrderStatus } = useData();
  
  if (!batch) return null;
  
  // Fonction pour obtenir la couleur de fond en fonction du type de cycle
  const getBgColor = (cycleName) => {
    switch(cycleName) {
      case 'A42': return 'bg-blue-500';
      case 'A35': return 'bg-purple-500';
      case 'E25': return 'bg-green-500';
      case 'A29': return 'bg-red-500';
      case 'E28': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };
  
  // Fonction pour obtenir l'icône d'état
  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <CheckCircle className="text-green-500" size={16} />;
      case 'in-progress': return <Clock className="text-blue-500" size={16} />;
      case 'waiting': return <Clock3 className="text-yellow-500" size={16} />;
      default: return <Clock3 className="text-gray-500" size={16} />;
    }
  };
  
  // Fonction pour obtenir le label d'état
  const getStatusLabel = (status) => {
    switch(status) {
      case 'completed': return 'Terminé';
      case 'in-progress': return 'En cours';
      case 'waiting': return 'En attente';
      default: return 'Non démarré';
    }
  };
  
  // Fonction pour obtenir la classe de couleur d'état
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'text-green-600';
      case 'in-progress': return 'text-blue-600';
      case 'waiting': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };
  
  // Fonction pour obtenir l'icône de priorité
  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'high': return <span className="px-1 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-800">!</span>;
      case 'medium': return <span className="px-1 py-0.5 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">!</span>;
      default: return null;
    }
  };
  
  // Calculer les statistiques de la fournée
  const completedCount = orders.filter(order => order.status === 'completed').length;
  const inProgressCount = orders.filter(order => order.status === 'in-progress').length;
  const waitingCount = orders.filter(order => order.status === 'waiting').length;
  const alertsCount = orders.filter(order => order.alert).length;
  
  return (
    <div 
      className={`bg-white rounded-lg shadow transition-all ${isSelected ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`}
      onClick={onSelect}
    >
      {/* En-tête de la fournée - avec marges réduites */}
      <div className={`px-3 py-2 rounded-t-lg flex justify-between items-center ${getBgColor(batch.cycleName)} bg-opacity-80 text-black`}>
        <h2 className="font-bold text-lg">
          {batch.batchNumber}
        </h2>
        <div className="flex items-center space-x-2">
          <div className="px-2 py-0.5 rounded-full text-lg font-medium bg-white bg-opacity-20">
          {resource?.name}
          </div>
          <div className="px-2 py-0.5 rounded-full text-lg font-medium bg-white bg-opacity-20">
            {batch.cycleName}
          </div>
        </div>
      </div>
      
      {/* Informations générales */}
      <div className="p-3 border-b bg-gray-50 flex justify-between items-center">
        <div className="flex items-center text-sm">
          <Clock className="mr-1 text-gray-800" size={14} />
          <span>{batch.startTime}-{batch.endTime}</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
            {completedCount}/{orders.length}
          </div>
          {alertsCount > 0 && (
            <div className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
              {alertsCount} alerte{alertsCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
      
      {/* Liste des OF - format compact sur une ligne */}
      <div className="p-3 overflow-y-auto" style={{ maxHeight: '335px' }}>
        {orders.length > 0 ? (
          <div className="space-y-1">
            {orders.map(order => (
              <div key={order.id} className="border rounded py-1 px-2">
                <div className="flex items-center justify-between">
                  {/* Partie gauche: icône statut, ID, nom */}
                  <div className="flex items-center space-x-2">
                    <div>{getStatusIcon(order.status)}</div>
                    <div className="font-medium text-sm">{order.id}</div>
                    <div className="text-sm text-gray-600">{order.name}</div>
                    {getPriorityIcon(order.priority)}
                  </div>
                  
                  {/* Partie droite: statut et boutons d'action */}
                  <div className="flex items-center space-x-2">
                    <div className={`uppercase text-xs font-semibold ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </div>
                    {/*{order.status !== 'completed' && (
                      <button 
                        className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateOrderStatus(order.id, 'completed');
                        }}
                      >
                        ✓
                      </button>
                    )}
                    {order.status === 'waiting' && (
                      <button 
                        className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateOrderStatus(order.id, 'in-progress');
                        }}
                      >
                        ▶
                      </button>
                    )}*/}
                  </div>
                </div>
                
                {/* Ligne d'alerte - seulement si une alerte existe */}
                {order.alert && (
                  <div className="mt-1 text-xs text-red-600 font-medium flex items-center">
                    <AlertTriangle size={12} className="mr-1" />
                    {order.alert}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            Aucun OF assigné à cette fournée
          </div>
        )}
      </div>
      
      {/* Barre de progression en bas */}
      <div className="p-3 border-t">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full" 
            style={{ width: `${(completedCount / Math.max(orders.length, 1)) * 100}%` }}
          ></div>
        </div>
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          <div>{completedCount} terminé{completedCount > 1 ? 's' : ''}</div>
          <div>{inProgressCount} en cours</div>
          <div>{waitingCount} en attente</div>
        </div>
      </div>
    </div>
  );
};

export default BatchDetail;